import React, { useContext, useEffect, useRef, useState } from 'react';
import pt from 'prop-types';
import Text from '../../../text';
import { combine } from '../../../../../helpers/styles';
import Icon from '../../../icon';
import useDebounce from '../../../../../hooks/useDebounce';
import { MicrocopyContext } from '../../../../../context/microcopy.context';
import { getCopy } from '../../../../../helpers/copy';
import * as styles from './styles.module.scss';

import { getAPIRoot } from '../../../../../helpers/fetch';
import Link from '../../../link';

const SearchField = ({
  initSearchTerm,
  placeholder,
  onSubmit,
  onReset,
  className,
  type,
  autoFocus,
  onBackdropClick,
  disableActiveBackdropStyles,
  small,
}) => {
  const inputRef = useRef();
  const microcopy = useContext(MicrocopyContext);
  const [isActive, setIsActive] = useState(false);
  const [autoSuggestion, setAutoSuggestion] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getUrl = async () => {
    let apiRoot = await getAPIRoot();

    switch (type) {
      case 'Companies':
        return apiRoot + process.env.GATSBY_API_FIND_COMPANY_SUGGESTION;
      case 'Talent':
        return apiRoot + process.env.GATSBY_API_FIND_SUGGESTION;
      case 'SIP':
        return apiRoot + process.env.GATSBY_API_FIND_SUGGESTION;
      case 'Jury':
        return apiRoot + process.env.GATSBY_API_FIND_JUROR_SUGGESTION;
      //TODO: correct ranking url
      case 'Ranking':
        return apiRoot + process.env.GATSBY_API_FIND_SUGGESTION;
      case 'Projects':
        return apiRoot + process.env.GATSBY_API_FIND_SUGGESTION;
      case 'Global':
        return apiRoot + process.env.GATSBY_API_SEARCH_SUGGESTION;
      default:
        return apiRoot + process.env.GATSBY_API_FIND_SUGGESTION;
    }
  };

  const getAutoSuggestion = async (searchTerm) => {
    const url = await getUrl();
    return fetch(`${url}/${searchTerm}`).then((res) => res.json());
  };

  useEffect(() => {
    if (searchTerm?.length >= 3 && debouncedSearchTerm) {
      getAutoSuggestion(debouncedSearchTerm.toLowerCase()).then((res) => {
        setAutoSuggestion(res);
      });
    } else {
      setAutoSuggestion([]);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setSearchTerm(initSearchTerm);
  }, [initSearchTerm]);

  const reset = () => {
    setIsActive(false);
    setSearchTerm('');
    onReset();
  };

  const cleanInput = () => {
    setSearchTerm('');
    inputRef.current.focus();
  };

  const change = (e) => {
    setSearchTerm(e.target.value);
  };

  const submit = (text) => {
    setIsActive(false);
    onSubmit(`${text.replace(/ \| (Company|Creatives)/, '')}`);
  };

  const keyPressHandler = (e) => {
    if (e.code === 'Enter') {
      submit(searchTerm);
      inputRef.current.blur();
    }
  };

  const handleLinkClick = (e) => {
    sessionStorage.clear();
  };

  const getFormattedSuggestion = (text, term) => {
    const re = new RegExp(decodeURIComponent(term), 'gi');
    return text.replace(re, (match) => `<b>${match}</b>`);
  };

  const handleBackDropClick = () => {
    setIsActive(false);
    if (onBackdropClick) {
      onBackdropClick();
    }
  };

  return (
    <>
      <div
        className={combine(
          styles.search,
          isActive && styles.isActive,
          className
        )}
      >
        <div className={styles.searchField}>
          <input
            type="text"
            name="search_field"
            placeholder={placeholder.toUpperCase()}
            className={combine(styles.searchInput, small && styles.small)}
            onChange={change}
            onFocus={() => setIsActive(true)}
            onKeyPress={keyPressHandler}
            value={searchTerm}
            autoComplete="off"
            ref={inputRef}
            autoFocus={autoFocus}
            data-test="search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={cleanInput}
              className={styles.closeBtn}
              data-test="search-clean-btn"
            >
              <Icon
                name="backspace"
                size="3x"
                className={combine(styles.closeIcon, small && styles.small)}
              />
            </button>
          )}
          {(isActive || !searchTerm) && (
            <button
              type="button"
              className={styles.searchBtn}
              onClick={() => submit(searchTerm)}
              data-test="search-search-btn"
            >
              <Icon
                name="search"
                className={combine(styles.searchIcon, small && styles.small)}
              />
            </button>
          )}
          {!isActive && searchTerm && (
            <button
              type="button"
              className={styles.searchBtn}
              onClick={reset}
              data-test="search-close-btn"
            >
              <Icon
                name="times"
                className={combine(styles.closeIcon, small && styles.small)}
              />
            </button>
          )}
        </div>

        {autoSuggestion && autoSuggestion.length > 0 && (
          <div className={styles.autoSuggestion}>
            <Text
              as="span"
              type="infotext"
              className={styles.autoSuggestionTitle}
            >
              {getCopy('label.quickLink', microcopy)}
            </Text>
            {autoSuggestion.map((item, index) => (
              <Link
                link={{ linkToPage: `${item.slug}?q=${searchTerm}` }}
                useGatsbyLink={false}
                onClick={handleLinkClick}
              >
                <Text
                  as="div"
                  type="p1"
                  className={styles.autoSuggestionContent}
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: getFormattedSuggestion(item.title, searchTerm),
                  }}
                />
              </Link>
            ))}
          </div>
        )}
      </div>
      <div
        className={combine(
          styles.backdrop,
          isActive && !disableActiveBackdropStyles && styles.isActive
        )}
        onClick={handleBackDropClick}
      />
    </>
  );
};

SearchField.propTypes = {
  searchTerm: pt.string,
  placeholder: pt.string,
  onReset: pt.func.isRequired,
  onSubmit: pt.func.isRequired,
  onBackdropClick: pt.func,
  className: pt.string,
  type: pt.oneOf([
    'Companies',
    'Projects',
    'Talent',
    'SIP',
    'Jury',
    'Ranking',
    'Global',
  ]),
  autoFocus: pt.bool,
  disableActiveBackdropStyles: pt.bool,
  small: pt.bool,
};

SearchField.defaultProps = {
  searchTerm: '',
  className: '',
  placeholder: 'What do you want to explore?',
  type: 'Projects',
  autoFocus: false,
  onBackdropClick: () => { },
  disableActiveBackdropStyles: false,
  small: false,
};

export default SearchField;
